import { Auth, PasswordValidationStatus, TotpSecret, validatePassword } from 'firebase/auth';
import { auth } from '../firebase';

export type PasswordRequirementKey = 'minLength' | 'lowercase' | 'uppercase' | 'number' | 'special';

export interface AccountPasswordRequirement {
  key: PasswordRequirementKey;
  label: string;
  met: boolean;
}

export interface AccountPasswordValidation {
  isValid: boolean;
  source: 'firebase' | 'fallback';
  requirements: AccountPasswordRequirement[];
  meetsMaxLength: boolean;
  errorCode?: string;
}

export interface TotpEnrollmentDraft {
  secret: TotpSecret;
  secretKey: string;
  qrCodeUrl: string;
  codeLength: number;
  codeIntervalSeconds: number;
  enrollmentCompletionDeadline: string;
}

export const ACCOUNT_PASSWORD_POLICY_COPY =
  'Hasło musi mieć minimum 8 znaków oraz zawierać małą literę, dużą literę, cyfrę i znak specjalny.';

const MAX_PASSWORD_LENGTH = 4096;

const REQUIREMENT_LABELS: Array<{ key: PasswordRequirementKey; label: string }> = [
  { key: 'minLength', label: '8+ znaków' },
  { key: 'lowercase', label: 'mała litera' },
  { key: 'uppercase', label: 'duża litera' },
  { key: 'number', label: 'cyfra' },
  { key: 'special', label: 'znak specjalny' },
];

const fallbackRequirementState = (password: string): Record<PasswordRequirementKey, boolean> => ({
  minLength: password.length >= 8,
  lowercase: /[a-z]/.test(password),
  uppercase: /[A-Z]/.test(password),
  number: /\d/.test(password),
  special: /[^A-Za-z0-9]/.test(password),
});

const buildRequirements = (
  state: Record<PasswordRequirementKey, boolean>
): AccountPasswordRequirement[] =>
  REQUIREMENT_LABELS.map((requirement) => ({
    ...requirement,
    met: state[requirement.key],
  }));

const buildFallbackValidation = (
  password: string,
  errorCode?: string
): AccountPasswordValidation => {
  const requirements = buildRequirements(fallbackRequirementState(password));
  const meetsMaxLength = password.length <= MAX_PASSWORD_LENGTH;

  return {
    isValid: meetsMaxLength && requirements.every((requirement) => requirement.met),
    source: 'fallback',
    requirements,
    meetsMaxLength,
    errorCode,
  };
};

const buildFirebaseValidation = (
  password: string,
  status: PasswordValidationStatus
): AccountPasswordValidation => {
  const fallbackState = fallbackRequirementState(password);
  const firebaseState: Record<PasswordRequirementKey, boolean> = {
    minLength: status.meetsMinPasswordLength ?? fallbackState.minLength,
    lowercase: status.containsLowercaseLetter ?? fallbackState.lowercase,
    uppercase: status.containsUppercaseLetter ?? fallbackState.uppercase,
    number: status.containsNumericCharacter ?? fallbackState.number,
    special: status.containsNonAlphanumericCharacter ?? fallbackState.special,
  };
  const requirements = buildRequirements(firebaseState);
  const meetsMaxLength = status.meetsMaxPasswordLength ?? password.length <= MAX_PASSWORD_LENGTH;

  return {
    isValid: Boolean(status.isValid && meetsMaxLength && requirements.every((item) => item.met)),
    source: 'firebase',
    requirements,
    meetsMaxLength,
  };
};

export const getPasswordPolicyHints = (
  validation?: AccountPasswordValidation,
  password = ''
): AccountPasswordRequirement[] =>
  validation?.requirements ?? buildFallbackValidation(password).requirements;

export const validateAccountPasswordFallback = (password: string): AccountPasswordValidation =>
  buildFallbackValidation(String(password || ''));

export const validateAccountPassword = async (
  password: string,
  authInstance: Auth = auth
): Promise<AccountPasswordValidation> => {
  const normalizedPassword = String(password || '');

  if (!normalizedPassword) {
    return buildFallbackValidation(normalizedPassword);
  }

  try {
    const status = await validatePassword(authInstance, normalizedPassword);
    return buildFirebaseValidation(normalizedPassword, status);
  } catch (error: any) {
    return buildFallbackValidation(normalizedPassword, String(error?.code || 'unknown'));
  }
};
